import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router";
import moment from "moment";

import { getLenderViewReport, downloadLenderReport, getPermissionedUser, getShareRecords } from "../lenderReportingDashboard.store";
import HeaderComponent from "../headerComponent";
import LenderDataHeader from "../lenderDataHeader";
import DeliverablesModule from "../deliverablesModule";
import { authorizedRequest, downloadFile } from "../../../utils/api";
import LenderViewAuth from "./lenderViewAuth";
import ApprovedPreviewCalculationsSection from "../approvedPreviewCalculationSection";

const PIN_VIEW_WIDTH = "1100px";
const LENDER_VIEW_WIDTH = "100%";
const THUMBNAIL_HEIGHT = 102;

const PageContainer = styled.div`
    width: 1120px;
    max-width: 1120px;
    margin: auto;
    display: flex;
    flex-direction: column;
    padding-bottom: 200px;
`;

const Banner = styled.div`
    height: 320px;
`;

const DeliverablesContainer = styled.div`
    flex: 2;
    width:100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PinLenderSectionWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
`;

const ThumbnailSectionWrapper = styled.div`
    z-index: 2;
    width: ${({ isLenderView }) => (isLenderView ? LENDER_VIEW_WIDTH : PIN_VIEW_WIDTH)};
    height: ${THUMBNAIL_HEIGHT / 2}px;
`;

const LenderDataHeaderWrapper = styled.div`
    width: ${PIN_VIEW_WIDTH};
`;

const LenderViewAuthSectionWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    width: ${PIN_VIEW_WIDTH};
`;

const LenderViewSectionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    box-sizing: border-box;
    padding: 0 1em 1em 1em;
    background-color: #FFFFFF;
`;

const StyledPreviewContainer = styled.div`
    width: 100%;
    height: 84px;
    margin: auto;
    background: #F7F2F2;
    padding: 0 1em;
    border: 1px solid #DADCE0;
    box-sizing: border-box;
`;

const StyledPreviewWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    justify-content: space-between;
    margin: auto;
    margin-top: 2em;
`;

const StyledPreviewText = styled.div`
    /* You are previewing the Lender View of this report. */
    width: 393px;
    height: 19px;

    font-family: 'Work Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    /* identical to box height */

    display: flex;
    align-items: center;

    color: #000000;
`;
const StyledExitPreview = styled.div`
    height: 19px;

    font-family: 'Work Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    /* identical to box height */

    display: flex;
    align-items: center;

    /* Text / LB Blue */

    color: #005AA9;
    cursor: pointer;
`;

const AccessRemovedText = styled.div`
    font-family: "Work Sans";
    font-weight: 400;
    font-size: 20px;
    line-height: 23.5px;
`;

const LenderView = (props) => {
  const { lenderView, match, authentication, history, company, permissionedUser, shareRecords } = props;

  const [lenderData, setLenderData] = useState(null);
  const { reportId } = match.params;
  const [pin, setPin] = useState("");
  const [isPrivilegedUser, setIsPrivilegedUser] = useState(false);
  const [reportUnshared, setReportUnshared] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [lenderDataTasksWithAttachments, setLenderDataTasksWithAttachments] = useState(null);

  useEffect(() => {
    props.getLenderViewReport({ reportId });
    props.getShareRecords({ rgiId: reportId });
  }, [reportId]);

  useEffect(() => {
    if (authentication.user) {
      props.getPermissionedUser({ contactId: authentication.user.contact?.id });
    }
  }, [authentication.user]);

  useEffect(() => {
    if (permissionedUser) {
      setIsPrivilegedUser(permissionedUser.isPrivileged);
    }
  }, [permissionedUser]);

  useEffect(() => {
    setLenderData(lenderView);
  }, [lenderView]);

  const getLenderDataWithFileAttachments = () => {
    const _tasksWithAttachments = lenderView?.tasks?.filter(task => (task?.files && task.files.length > 0));
    const _lenderData = {...lenderView, tasks: _tasksWithAttachments};
    setLenderDataTasksWithAttachments(_lenderData);
  };

  useEffect(() => {
    getLenderDataWithFileAttachments();
  }, [lenderView]);

  const callForDataWithPin = (pinInput) => {
    props.getLenderViewReport({ reportId, pin: pinInput });
    setPin(pinInput);
  };

  useEffect(() => {
    if (shareRecords && shareRecords.length) {
      setReportUnshared(shareRecords[0].unshared);
    }
  }, [shareRecords]);

  useEffect(() => {
    if(lenderData && lenderData.expirationDate) {
      const expiration = moment(lenderData.expirationDate).format("LL");
      const today = moment().utc().format("LL");
      const diff = moment(expiration).diff(today, "days");
      setIsExpired(diff < 0);
    }
  }, [lenderData]);

  const checkIsDscrAndDebtYield = () => {
    const rgiReportComponentInstances = lenderData.taskDetails.reportGroupInstance.Items[0].reportComponentInstances;
    const resObj = { isCalculateDscr: false, isCalculateDy: false };
    const noiComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "NOI");
    if(!noiComponent){
      return resObj;
    }
    const dscrComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DSCR");
    const debtYieldComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DebtYield");
    resObj.isCalculateDscr = !!dscrComponent;
    resObj.isCalculateDy = !!debtYieldComponent;

    return resObj;
  };

  const handleDownload = (fileIds, taskName) =>
    fileIds.length === 1
      ? downloadFile(`lrd/lender/${reportId}/files/${[fileIds]}?authCode=${pin}`)
      : props.downloadLenderReport({ rgiId: reportId, pin, taskName, moduleFiles: JSON.stringify(fileIds) });

    const showReportCalculationsForApproved = useMemo(() => {
      if (!lenderData || !lenderData.taskDetails || !lenderData.taskDetails.reportGroupInstance) {
        return null;
      }

      const rgiReportComponentInstances = lenderData.taskDetails.reportGroupInstance.Items[0].reportComponentInstances;

      const noiComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "NOI");
      const dscrComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DSCR");
      const debtYieldComponent = rgiReportComponentInstances.find(repComponent => repComponent.type === "DebtYield");

      if (!noiComponent) {
        return false;
      }

      return (!!dscrComponent || !!debtYieldComponent);
    }, [lenderData]);

  const downloadReport = async () => {
    const {shouldIncludeDscrInReport, shouldIncludeDebtYieldInReport} = lenderData.taskDetails.reportGroupInstance.Items[0];

    const isDscrDyCalculations = checkIsDscrAndDebtYield();
    const reportFilesPromises = [];
    const authToken = pin ? `authCode_${pin}_reportId_${reportId}` : null;
    if (typeof(shouldIncludeDscrInReport) === "undefined" && isDscrDyCalculations.isCalculateDscr || shouldIncludeDscrInReport) {
      reportFilesPromises.push(authorizedRequest(`/report-groups/instances/createExcelDocuments/${reportId}/DSCR/${lenderData.taskDetails.deal.id}/${encodeURIComponent(lenderData.taskDetails.deal.name)}?authCode=${pin}`, "GET", null, null, true, authToken));
    }
    if (typeof(shouldIncludeDebtYieldInReport) === "undefined" && isDscrDyCalculations.isCalculateDy || shouldIncludeDebtYieldInReport) {
      reportFilesPromises.push(authorizedRequest(`/report-groups/instances/createExcelDocuments/${reportId}/DebtYield/${lenderData.taskDetails.deal.id}/${encodeURIComponent(lenderData.taskDetails.deal.name)}?authCode=${pin}`, "GET", null, null, true, authToken));
    }

    const reportFiles = await Promise.all(reportFilesPromises);

    const filteredReportFiles = reportFiles.filter((file) => file.key);
    props.downloadLenderReport({ rgiId: reportId, pin, reportFiles: JSON.stringify(filteredReportFiles) });
  };


  return (
    <PageContainer>
      {lenderData?.approver && (
        <>
          <Banner>
            <HeaderComponent banner={company?.bannerImage} logo={company?.logoImage} />
          </Banner>
          <LenderViewSectionWrapper>
            <ThumbnailSectionWrapper isLenderView>
            </ThumbnailSectionWrapper>
            <div style={{ backgroundColor: "white", width: LENDER_VIEW_WIDTH }}>
              <LenderDataHeader downloadReport={downloadReport} authenticated lenderData={lenderData} isLenderView />
            </div>
          </LenderViewSectionWrapper>
        </>
      )}
      {authentication?.authed && (
        <StyledPreviewContainer>
          <StyledPreviewWrapper>
            <StyledPreviewText>You are previewing the Lender View of this report</StyledPreviewText>
            <StyledExitPreview onClick={() => history.goBack()}>Exit Preview</StyledExitPreview>
          </StyledPreviewWrapper>
        </StyledPreviewContainer>
      )}
      {!lenderData?.approver && !authentication?.authed && (
        <>
          <Banner>
            <HeaderComponent banner={company?.bannerImage} logo={company?.logoImage} />
          </Banner>
          <PinLenderSectionWrapper>
            <ThumbnailSectionWrapper>
            </ThumbnailSectionWrapper>
            <LenderDataHeaderWrapper>
              <LenderDataHeader lenderData={lenderData} isLenderView reportUnshared={reportUnshared} />
            </LenderDataHeaderWrapper>
            <LenderViewAuthSectionWrapper>
              {reportUnshared && (<AccessRemovedText>Access to this report has been removed.<br /> Please contact the sender for details.</AccessRemovedText>)}
              {(!reportUnshared && !isExpired) && <LenderViewAuth refreshDataWithPin={callForDataWithPin} showPinError={lenderData?.invalidPin} />}
              {(!reportUnshared && isExpired) && <AccessRemovedText>This link is no longer active.<br /> Please contact the sender for renewed access.</AccessRemovedText>}
            </LenderViewAuthSectionWrapper>
          </PinLenderSectionWrapper>
        </>
      )}
      {lenderData?.approver && showReportCalculationsForApproved && <ApprovedPreviewCalculationsSection taskDetails={lenderData.taskDetails} />}
      {lenderDataTasksWithAttachments?.approver &&
        (lenderDataTasksWithAttachments?.tasks?.length > 0) &&
        lenderDataTasksWithAttachments?.tasks.map((task, idx) => (
          <DeliverablesContainer key={idx}>
            <DeliverablesModule handleDownload={handleDownload} type={task.taskName} files={task.files || []} ableToSeePrivilegedInfo={isPrivilegedUser || authentication?.isSystemAdmin || pin} task={task} />
          </DeliverablesContainer>
        ))}
    </PageContainer>
  );
};

const mapStateToProps = ({ lenderReportingDashboard, authentication }) => ({ ...lenderReportingDashboard, authentication });

export default compose(
  withRouter,
  connect(mapStateToProps, {
    getLenderViewReport,
    downloadLenderReport,
    getPermissionedUser,
    getShareRecords
  })
)(LenderView);
